import { Store } from 'redux'
import { Entity } from '../app/entity'
import { ApplicationState } from '../redux/ApplicationState'
import { TracesState } from '../redux/traces/TracesState'
import { FULFILLED } from '../redux/V2/action-helper'
import { WRITE_SHARED_COMPARISON_INFO, WRITE_SHARED_INFO } from '../redux/V2/Trace/actions'

/**
 * Loader classes like this consilidate all loading traces for a domain
 * concept.
 *
 * They define one (or multiple) load() methods which execute an AJAX call
 * through the Api. You may implement parameter remapping and cache checks in
 * here. For the loaded date there should be a dispatched action through Redux.
 *
 * The Loader also defines a (static) method which handles its own actions and
 * applies the corresponding changes to the global store state.
 */
const Loader = function(store: Store<ApplicationState>, api) {
    this.store = store
    this.api = api

    this.load = parameters => {
        const globalState = this.store.getState().traces
        if (globalState.traces[parameters.profile] && globalState.traces[parameters.profile].isCurrent()) {
            return
        }

        this.api.trigger('XhprofProfilerBundle.Profile.traceData', parameters, parameters.profile)

        if (parameters.base) {
            this.api.trigger(
                'XhprofProfilerBundle.Profile.traceData',
                { ...parameters, profile: parameters.base, base: undefined },
                parameters.base,
            )
        }
    }

    this.loadToken = parameters => {
        const globalState = this.store.getState().traces
        if (globalState.token && globalState.token.isCurrent()) {
            return
        }

        this.api.trigger('XhprofOrganizationBundle.Application.token', parameters)
    }

    this.loadFunctionCompare = parameters => {
        const globalState = this.store.getState().traces
        if (globalState.functions && globalState.functions.isCurrent()) {
            return
        }

        this.api.trigger('XhprofProfilerBundle.Profile.functionCompareData', parameters)
    }
}

const initialGlobalState: TracesState = {
    traces: {},
    token: null,
    functions: null,
}

Loader.handleAction = (globalState: TracesState = initialGlobalState, action) => {
    let clonedGlobalState = {}

    switch (action.type) {
        case 'TIDEWAYS_ROUTE':
            return {
                traces: Entity.purgeMap(globalState.traces),
                token: null,
                functions: null,
            }
        case 'Profile.traceData.success':
            clonedGlobalState = { ...globalState.traces }
            clonedGlobalState[action.id] = new Entity(action.data, 3600)

            return {
                ...globalState,
                traces: clonedGlobalState,
            }

        case 'Profile.traceData.error':
            clonedGlobalState = { ...globalState.traces }
            clonedGlobalState[action.id] = new Entity().setError(action.error)

            return {
                ...globalState,
                traces: clonedGlobalState,
            }
        case 'Application.token.success':
            return {
                ...globalState,
                token: new Entity(action.data, 300),
            }
        case 'Application.token.error':
            return {
                ...globalState,
                token: new Entity().setError(action.error),
            }
        case 'Profile.functionCompareData.success':
            return {
                ...globalState,
                functions: new Entity(action.data, 3600),
            }
        case 'Profile.functionCompareData.error':
            return {
                ...globalState,
                functions: new Entity().setError(action.error),
            }
        case 'TIDEWAYS_SHARED_TRACE':
            return {
                ...globalState,
                traces: {
                    ...globalState.traces,
                    [action.trace.profile.id]: new Entity(action.trace, 3600),
                },
            }
        case FULFILLED(WRITE_SHARED_INFO): {
            const { id, hasActiveShare, sharedTraceInfo } = action.payload

            const newData = {
                ...globalState.traces[id].data,
                profile: {
                    ...globalState.traces[id].data?.profile,
                    hasActiveShare,
                    sharedTraceInfo,
                },
            }

            return {
                ...globalState,
                traces: {
                    ...globalState.traces,
                    [id]: new Entity(newData),
                },
            }
        }
        case FULFILLED(WRITE_SHARED_COMPARISON_INFO): {
            const { targetId, baseId, hasActiveShare, targetSharedTraceInfo, baseSharedTraceInfo } = action.payload

            const newTargetData = {
                ...globalState.traces[targetId].data,
                profile: {
                    ...globalState.traces[targetId].data?.profile,
                    hasActiveShare,
                    sharedTraceInfo: targetSharedTraceInfo,
                },
            }
            const newBaseData = {
                ...globalState.traces[baseId].data,
                profile: {
                    ...globalState.traces[baseId].data?.profile,
                    hasActiveShare,
                    sharedTraceInfo: baseSharedTraceInfo,
                },
            }

            return {
                ...globalState,
                traces: {
                    ...globalState.traces,
                    [targetId]: new Entity(newTargetData),
                    [baseId]: new Entity(newBaseData),
                },
            }
        }
    }

    return globalState
}

export default Loader
